import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, UserPen, Package, Settings, Search, MessageSquare } from 'lucide-react';

const Header = ({
  activeSection = 'profile',
  onSearch = null,
  searchPlaceholder = 'Search products...',
  searchValue = '',
  onSearchChange = null
}) => {
  const navigate = useNavigate();
  const [internalSearchQuery, setInternalSearchQuery] = useState('');

  // Use external search state if provided, otherwise use internal state
  const searchQuery = onSearchChange ? searchValue : internalSearchQuery;
  const setSearchQuery = onSearchChange ? onSearchChange : setInternalSearchQuery;

  const navigationItems = [
    { id: 'wishlist', icon: Heart, label: 'Wishlist', route: '/wishlist' },
    { id: 'cart', icon: ShoppingCart, label: 'Shopping cart', route: '/cart' },
    { id: 'profile', icon: UserPen, label: 'Edit profile', route: '/profile' },
    { id: 'orders', icon: Package, label: 'Orders', route: '/orders' },
    { id: 'report', icon: MessageSquare, label: 'Report Issue', route: '/report' },
    { id: 'settings', icon: Settings, label: 'Settings', route: '/settings' }
  ];

  const handleNavigation = (route) => {
    navigate(route);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    // If there's a custom search handler (for current page context), use it
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      // Default behavior: navigate to products page
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const getSearchPlaceholder = () => {
    switch (activeSection) {
      case 'wishlist':
        return 'Search wishlist items...';
      case 'cart':
        return 'Search cart items...';
      case 'orders':
        return 'Search orders...';
      default:
        return searchPlaceholder;
    }
  };

  const shouldShowSearch = () => {
    return !['profile', 'settings', 'report'].includes(activeSection);
  };

  const renderNavigationIcon = (item) => {
    const Icon = item.icon;
    const isActive = activeSection === item.id;

    return (
      <div key={item.id} className="relative flex items-center justify-center">
        {isActive && (
          <>
            <div className="absolute w-[70px] h-[70px] bg-orange-50 rounded-full max-md:w-[62px] max-md:h-[62px]" />
            <div className="absolute w-[58px] h-[58px] bg-teal-800 rounded-full shadow-lg max-md:w-[50px] max-md:h-[50px]" />
          </>
        )}

        <button
          onClick={() => handleNavigation(item.route)}
          className={`relative  p-2 transition-colors rounded-lg ${
            isActive
              ? 'text-orange-100'
              : 'text-gray-400 hover:text-white hover:bg-gray-800 hover:bg-opacity-20'
          }`}
          aria-label={item.label}
        >
          <Icon className="w-[30px] h-[30px] max-md:w-[22px] max-md:h-[22px]" />
        </button>
      </div>
    );
  };

  return (
    <header className="w-full mt-6 bg-orange-50 bg-opacity-90">
      <nav className="relative h-[104px] sm:h-[59px] lg:h-[104px] mx-auto w-[80%] max-md:w-[90%]">

        {/* Navbar background */}
        <div className="absolute top-0 left-0 bg-neutral-900 h-[59px] rounded-[30px_30px_8px_8px] w-full" />

        {/* Desktop Navigation */}
        <div className="hidden sm:flex justify-between items-center absolute top-0 left-0 right-0 h-[59px] px-10">

          {/* Search Bar - Conditionally rendered */}
          {shouldShowSearch() ? (
            <form onSubmit={handleSearch} className="flex items-center bg-white rounded-full px-3 py-1 w-[250px]">
              <Search className="w-4 h-4 text-gray-500 mr-2" />
              <input
                type="text"
                placeholder={getSearchPlaceholder()}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="outline-none text-sm w-full"
              />
            </form>
          ) : (
            <div className="w-[250px]"></div>
          )}

          {/* Icons */}
          <div className="flex justify-between items-center gap-8">
            {navigationItems.map((item) => renderNavigationIcon(item))}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="flex justify-around items-center px-4 sm:hidden absolute top-0 left-0 right-0 h-[50px]">
          {navigationItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.route)}
              className={`p-2 rounded ${
                activeSection === item.id
                  ? 'text-white bg-teal-800 bg-opacity-80'
                  : 'text-gray-400'
              }`}
              aria-label={item.label}
            >
              <item.icon className="w-7 h-7" />
            </button>
          ))}
        </div>

        {/* Mobile Search Bar - Conditionally rendered */}
        {shouldShowSearch() && (
          <div className="sm:hidden absolute top-[60px] left-0 right-0 px-4">
            <form onSubmit={handleSearch} className="flex items-center bg-white mt-6 sm:mt-6 rounded-full px-3 py-2 w-full">
              <Search className="w-4 h-4  text-gray-500 mr-2" />
              <input
                type="text"
                placeholder={getSearchPlaceholder()}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="outline-none text-sm w-full"
              />
            </form>
          </div>
        )}

      </nav>
    </header>
  );
};

export default Header;
